import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { useLang } from "../lib/i18n";
import { Overline } from "../components/ui-vision";
import { DateField, SlotField } from "../components/DateTimeFields";

const SLOTS = ["09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00", "17:00", "18:00"];

const Field = ({ label, type = "text", value, onChange, testid, textarea, required, placeholder, error }) => (
  <label className="block group">
    <span className="text-xs uppercase tracking-[0.2em] text-white/40 group-focus-within:text-accent-yellow transition-colors duration-300">
      {label}
      {required && <span className="text-accent-yellow ml-1">*</span>}
    </span>
    {textarea ? (
      <textarea
        rows={2}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testid}
        className="w-full bg-transparent border-b border-white/20 py-2 mt-1 text-xs text-white outline-none focus:border-accent-yellow transition-colors duration-300 resize-none placeholder:text-white/25"
      />
    ) : (
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testid}
        className={`w-full bg-transparent border-b py-2 mt-1 text-xs text-white outline-none focus:border-accent-yellow transition-colors duration-300 placeholder:text-white/25 ${
          error ? "border-accent-yellow" : "border-white/20"
        }`}
      />
    )}
    {error && (
      <span data-testid={`${testid}-error`} className="mt-1.5 block text-xs text-accent-yellow">
        {error}
      </span>
    )}
  </label>
);

const FieldLabel = ({ children, required }) => (
  <span className="block text-xs uppercase tracking-[0.2em] text-white/40 mb-1">
    {children}
    {required && <span className="text-accent-yellow ml-1">*</span>}
  </span>
);

export default function RendezVous() {
  const { lang, t } = useLang();
  const r = t.rdv;

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split("T")[0];

  const [date, setDate] = useState("");
  const [slot, setSlot] = useState("");
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", phone: "", address: "", comment: "" });
  const [errors, setErrors] = useState({});
  const [done, setDone] = useState(null);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!date) errs.date = r.errDate;
    if (!slot) errs.slot = r.errSlot;
    if (!form.phone.trim()) errs.phone = r.errPhone;
    if (!form.address.trim()) errs.address = r.errAddress;

    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rendez-vous`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ date, slot, ...form, lang }),
      });
    } catch (err) {
      console.error("Rendez-vous submission failed", err);
    }
    const formatted = new Date(date + "T00:00:00").toLocaleDateString(lang === "fr" ? "fr-FR" : "nl-BE", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
    setDone({ date: formatted, slot });
  };

  return (
    <section className="pt-28 md:pt-32 pb-16 md:pb-20 max-w-[1440px] mx-auto px-6 md:px-12" data-testid="rendezvous">
      <div className="max-w-3xl mx-auto space-y-6 lg:space-y-10">
        <div>
          <div className="lg:hidden rounded-2xl border border-white/10 bg-white/[0.03] px-5 py-4" data-testid="rdv-mobile-title">
            <Overline>{r.title.replace(/\.$/, "")}</Overline>
            <p className="mt-2 text-sm text-white/55 font-light leading-relaxed">{r.lead}</p>
          </div>
          <div className="hidden lg:block rounded-2xl border border-white/10 bg-white/[0.03] px-5 py-4" data-testid="rdv-intro-card">
            <Overline>{r.title.replace(/\.$/, "")}</Overline>
          </div>
        </div>

        <div>
          <AnimatePresence mode="wait">
            {done ? (
              <motion.div
                key="confirm"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="border border-accent-yellow/40 bg-white/[0.03] rounded-[14px] p-10 md:p-14"
                data-testid="rdv-confirmation"
              >
                <div className="w-12 h-12 rounded-full bg-accent-yellow text-ink flex items-center justify-center mb-8">
                  <Check size={22} />
                </div>
                <h2 className="font-display text-3xl md:text-4xl font-medium mb-4">{r.successTitle}</h2>
                <p className="text-xl text-white/80 mb-3">
                  {r.on} <span className="text-accent-yellow">{done.date}</span> {r.at}{" "}
                  <span className="text-accent-yellow">{done.slot}</span>
                </p>
                <p className="text-white/55 font-light">{r.successNote}</p>
              </motion.div>
            ) : (
              <motion.form
                key="form"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                onSubmit={submit}
                noValidate
                className="space-y-6"
                data-testid="rdv-form"
              >
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <FieldLabel required>{r.dateLabel}</FieldLabel>
                    <DateField
                      value={date}
                      onChange={setDate}
                      min={minDate}
                      placeholder={r.chooseDatePlaceholder}
                      error={errors.date}
                      testid="rdv-date"
                      lang={lang}
                    />
                    {errors.date && (
                      <span data-testid="rdv-date-error" className="mt-1.5 block text-xs text-accent-yellow">
                        {errors.date}
                      </span>
                    )}
                  </div>

                  <div>
                    <FieldLabel required>{r.timeLabel}</FieldLabel>
                    <SlotField
                      value={slot}
                      onChange={setSlot}
                      options={SLOTS}
                      placeholder={r.chooseSlot}
                      error={errors.slot}
                      testid="rdv-slot"
                    />
                    {errors.slot && (
                      <span data-testid="rdv-slot-error" className="mt-1.5 block text-xs text-accent-yellow">
                        {errors.slot}
                      </span>
                    )}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <Field label={r.firstName} value={form.firstName} onChange={upd("firstName")} testid="rdv-firstname" placeholder={r.phFirstName} />
                  <Field label={r.lastName} value={form.lastName} onChange={upd("lastName")} testid="rdv-lastname" placeholder={r.phLastName} />
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <Field label={r.email} type="email" value={form.email} onChange={upd("email")} testid="rdv-email" placeholder={r.phEmail} />
                  <Field label={r.phone} type="tel" value={form.phone} onChange={upd("phone")} testid="rdv-phone" placeholder={r.phPhone} required error={errors.phone} />
                </div>
                <Field label={r.address} value={form.address} onChange={upd("address")} testid="rdv-address" placeholder={r.phAddress} required error={errors.address} />
                <Field label={r.comment} value={form.comment} onChange={upd("comment")} testid="rdv-comment" placeholder={r.phComment} textarea />

                <button
                  type="submit"
                  data-testid="rdv-submit"
                  className="group inline-flex items-center gap-3 px-8 py-4 rounded-xl bg-accent-yellow text-ink font-semibold hover:bg-white transition-colors duration-300"
                >
                  {r.submit}
                  <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
